import styled from 'styled-components';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import fonts from '../../design-system/fonts';
import colors from '../../design-system/colors';

export const HeaderContainer = styled(motion.header)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1000;
  background: #000000;
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.2);
  transition: all 0.3s cubic-bezier(0.25, 0.1, 0.25, 1);
  padding: 0 40px;
  width: 100%;
  box-sizing: border-box;

  @media (max-width: 1024px) {
    padding: 0 30px;
  }

  @media (max-width: 768px) {
    padding: 0 20px;
    position: fixed;
    left: 0;
    right: 0;
    width: 100vw;
    max-width: 100vw;
  }
`;

export const HeaderContent = styled.div`
  width: 100%;
  margin: 0 auto;
  padding: 12px 0px;
  display: grid;
  grid-template-columns: auto 1fr auto;
  align-items: center;
  position: relative;

  @media (max-width: 768px) {
    padding: 10px 0;
    gap: 10px;
    align-items: center;
  }
`;

export const Logo = styled(motion.div)`
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  height: 40px;
  position: relative;
  transition: all 0.3s ease;
  flex-shrink: 0;
  z-index: 1;

  &:hover {
    transform: scale(1.05);
  }

  @media (max-width: 768px) {
    height: 36px;
    flex-shrink: 0;
  }
`;

export const LogoImage = styled.img`
  height: 100%;
  width: auto;
  max-width: 160px;
  object-fit: contain;
  object-position: left center;
  display: block;
  transition: all 0.3s cubic-bezier(0.25, 0.1, 0.25, 1);
  filter: brightness(0) invert(1);

  ${Logo}:hover & {
    opacity: 0.85;
    transform: scale(1.02);
  }

  @media (max-width: 768px) {
    max-width: 100px;
    height: auto;
  }
`;

export const LogoText = styled.div`
  font-size: 24px;
  font-weight: 700;
  color: ${colors.sapphire};
  text-transform: uppercase;
  letter-spacing: -1px;
  font-family: ${fonts.headline};
`;

export const Nav = styled.nav`
  display: flex;
  gap: 48px;
  align-items: center;
  justify-content: center;
  width: 100%;
  grid-column: 2;
  justify-self: center;
  position: relative;
  z-index: 10;
  pointer-events: auto;

  @media (max-width: 1200px) {
    gap: 32px;
  }

  @media (max-width: 1024px) {
    gap: 24px;
  }

  @media (max-width: 768px) {
    display: none;
  }
`;

export const NavLink = styled(motion(Link))`
  font-size: 16px;
  font-weight: 500;
  color: #ffffff;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  text-decoration: none;
  position: relative;
  padding: 8px 12px;
  transition: all 0.3s cubic-bezier(0.25, 0.1, 0.25, 1);
  font-family: ${fonts.body};
  white-space: nowrap;
  cursor: pointer;
  z-index: 11;
  pointer-events: auto;
  display: block;

  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    width: 0;
    height: 2px;
    background: linear-gradient(
      90deg,
      ${colors.sapphire},
      ${colors.frenchBlue}
    );
    transition: width 0.3s cubic-bezier(0.25, 0.1, 0.25, 1);
  }

  &:hover {
    color: ${colors.sapphire};
    transform: translateY(-2px);

    &::after {
      width: 100%;
    }
  }

  @media (max-width: 1024px) {
    font-size: 15px;
  }
`;

export const ProductsNavContainer = styled(motion.div)`
  position: relative;
  display: inline-block;
  z-index: 11;
  pointer-events: auto;
`;

export const ProductsNavLink = styled(Link)`
  font-size: 16px;
  font-weight: 500;
  color: #ffffff;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  text-decoration: none;
  position: relative;
  padding: 8px 12px;
  transition: all 0.3s cubic-bezier(0.25, 0.1, 0.25, 1);
  font-family: ${fonts.body};
  white-space: nowrap;
  cursor: pointer;
  z-index: 12;
  pointer-events: auto;
  display: block;

  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    width: 0;
    height: 2px;
    background: linear-gradient(
      90deg,
      ${colors.sapphire},
      ${colors.frenchBlue}
    );
    transition: width 0.3s cubic-bezier(0.25, 0.1, 0.25, 1);
  }

  &:hover {
    color: ${colors.sapphire};
    transform: translateY(-2px);

    &::after {
      width: 100%;
    }
  }

  @media (max-width: 1024px) {
    font-size: 15px;
  }
`;

export const IconsContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;
  margin-left: 40px;

  @media (max-width: 1024px) {
    margin-left: 24px;
    gap: 16px;
  }

  @media (max-width: 768px) {
    display: none;
  }
`;

export const IconButton = styled(motion.div)`
  display: flex;
  align-items: center;
  gap: 8px;
  color: white;
  cursor: pointer;
`;

export const IconText = styled.span`
  font-size: 14px;
  color: white;
  font-family: ${fonts.body};
`;

export const HamburgerButton = styled(motion.button)`
  display: none;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  color: #ffffff;
  cursor: pointer;
  padding: 8px;
  transition: all 0.3s ease;
  position: relative;
  z-index: 1;
  min-width: 40px;
  min-height: 40px;
  flex-shrink: 0;
  box-sizing: border-box;

  @media (max-width: 768px) {
    display: flex;
    align-items: center;
    justify-content: center;
    min-width: 40px;
    width: 40px;
    height: 40px;
    padding: 8px;
    flex-shrink: 0;
  }

  &:hover {
    background: rgba(255, 255, 255, 0.1);
    border-color: #ffffff;
    color: #ffffff;
  }

  &:active {
    background: rgba(255, 255, 255, 0.15);
  }

  svg {
    width: 24px;
    height: 24px;
    transition: transform 0.3s ease;
    stroke: currentColor;
  }
`;

export const MobileMenu = styled(motion.div)`
  display: none;
  background: #000000;
  border-top: 1px solid rgba(255, 255, 255, 0.08);
  padding: 24px 20px;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 1px;
    background: linear-gradient(
      90deg,
      transparent 0%,
      ${colors.sapphire} 50%,
      transparent 100%
    );
    opacity: 0.3;
  }

  @media (max-width: 768px) {
    display: block;
  }
`;

export const MobileNav = styled(motion.div)`
  display: flex;
  flex-direction: column;
  gap: 0;
`;

export const MobileNavLink = styled(Link)`
  font-size: 15px;
  font-weight: 500;
  color: #ffffff;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  text-decoration: none;
  padding: 16px 0;
  transition: color 0.2s ease;
  font-family: ${fonts.body};
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
  display: block;

  &:last-child {
    border-bottom: none;
  }

  &:hover,
  &:active {
    color: ${colors.sapphire};
  }
`;

export const SearchContainer = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  z-index: 1;
  pointer-events: auto;

  @media (max-width: 768px) {
    width: 100%;
    max-width: none;
    min-width: 0;
    flex: 1;
    justify-content: center;
  }
`;

export const SearchInput = styled.input`
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 24px;
  padding: 8px 16px;
  padding-right: 40px;
  color: #ffffff;
  font-size: 14px;
  font-family: ${fonts.body};
  width: 250px;
  transition: all 0.3s ease;
  box-sizing: border-box;

  &::placeholder {
    color: rgba(255, 255, 255, 0.5);
  }

  &:focus {
    outline: none;
    border-color: ${colors.sapphire};
    background: rgba(255, 255, 255, 0.15);
  }

  @media (max-width: 1024px) {
    width: 200px;
    font-size: 13px;
  }

  @media (max-width: 768px) {
    width: 100%;
    max-width: 280px;
    font-size: 16px; /* Prevent iOS zoom and ensure proper centering */
    padding: 9px 14px;
    padding-right: 36px;
    min-width: 0;
    -webkit-appearance: none;
    appearance: none;
  }
`;

export const SearchResults = styled(motion.div)`
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  background: #0f0f0f;
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 12px;
  min-width: 320px;
  max-width: 420px;
  max-height: 400px;
  overflow-y: auto;
  z-index: 1000;
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.7),
    0 0 0 1px rgba(255, 255, 255, 0.05);
  padding: 8px;
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);

  /* Custom scrollbar */
  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.15);
    border-radius: 10px;

    &:hover {
      background: rgba(255, 255, 255, 0.25);
    }
  }

  @media (max-width: 1024px) {
    min-width: 280px;
    max-width: 320px;
  }

  @media (max-width: 768px) {
    right: auto;
    left: 0;
    min-width: 100%;
    max-width: 100%;
    max-height: 250px;
  }
`;

export const SearchResultItem = styled.div`
  padding: 14px 16px;
  color: rgba(255, 255, 255, 0.95);
  font-size: 14px;
  font-weight: 500;
  font-family: ${fonts.body};
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.25, 0.1, 0.25, 1);
  border-radius: 10px;
  margin-bottom: 8px;
  background: linear-gradient(
    135deg,
    rgba(255, 255, 255, 0.05),
    rgba(255, 255, 255, 0.02)
  );
  border: 1px solid rgba(255, 255, 255, 0.08);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3), 0 1px 2px rgba(0, 0, 0, 0.2),
    inset 0 1px 0 rgba(255, 255, 255, 0.05);
  position: relative;
  overflow: hidden;
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 1px;
    background: linear-gradient(
      90deg,
      transparent,
      rgba(255, 255, 255, 0.15),
      transparent
    );
    opacity: 0;
    transition: opacity 0.3s ease;
  }

  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(
      90deg,
      transparent,
      rgba(28, 80, 196, 0.1),
      transparent
    );
    transition: left 0.5s ease;
  }

  &:last-child {
    margin-bottom: 0;
  }

  &:hover {
    background: linear-gradient(
      135deg,
      rgba(28, 80, 196, 0.2),
      rgba(28, 80, 196, 0.1)
    );
    color: #ffffff;
    border-color: rgba(28, 80, 196, 0.5);
    box-shadow: 0 6px 20px rgba(28, 80, 196, 0.4),
      0 2px 8px rgba(28, 80, 196, 0.3), 0 0 0 1px rgba(28, 80, 196, 0.3),
      inset 0 1px 0 rgba(255, 255, 255, 0.1);
    transform: translateY(-3px);

    &::before {
      opacity: 1;
    }

    &::after {
      left: 100%;
    }
  }

  &:active {
    transform: translateY(-1px);
    box-shadow: 0 3px 12px rgba(28, 80, 196, 0.3),
      0 0 0 1px rgba(28, 80, 196, 0.2);
  }

  @media (max-width: 1024px) {
    font-size: 13px;
    padding: 12px 14px;
    margin-bottom: 6px;
    border-radius: 8px;
  }
`;
